/**
 * Vanilla YouTube facade init shared by faff (live) and the React component
 * (preview). Preview keeps React JS, so componentDidMount can wire this up
 * immediately; live pages strip React and rely on faff instead.
 *
 */

const MOBILE_MAX_WIDTH = '81.25em' // lg-xl
const YT_STATE_PLAYING = 1

let youtubeApiPromise = null
let youtubeResourcesWarmed = false

function loadYoutubeApi (doc) {
  if (youtubeApiPromise) return youtubeApiPromise

  youtubeApiPromise = new Promise((resolve) => {
    if (typeof window !== 'undefined' && window.YT && window.YT.Player) {
      resolve()
      return
    }

    const previousReady = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousReady === 'function') previousReady()
      resolve()
    }

    if (!doc.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const script = doc.createElement('script')
      script.src = 'https://www.youtube.com/iframe_api'
      doc.head.appendChild(script)
    }
  })

  return youtubeApiPromise
}

function warmupYoutubeApi (doc) {
  if (youtubeResourcesWarmed) return loadYoutubeApi(doc)
  youtubeResourcesWarmed = true

  ;[
    'https://www.youtube.com',
    'https://www.google.com',
    'https://i.ytimg.com'
  ].forEach((href) => {
    if (doc.querySelector(`link[data-youtube-preconnect="${href}"]`)) return
    const link = doc.createElement('link')
    link.rel = 'preconnect'
    link.href = href
    link.crossOrigin = ''
    link.setAttribute('data-youtube-preconnect', href)
    doc.head.appendChild(link)
  })

  return loadYoutubeApi(doc)
}

export function initYoutubeVideosOnPage (doc) {
  doc.querySelectorAll('[data-js-youtube-video]').forEach((moduleHTML) => {
    initYoutubeVideo(doc, moduleHTML)
  })
}

function isInOrNearViewport (element, marginPx = 300) {
  if (typeof window === 'undefined') return true
  const rect = element.getBoundingClientRect()
  return rect.bottom > -marginPx && rect.top < window.innerHeight + marginPx
}

function isIOSDevice () {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

function isFirstPageModule (moduleHTML) {
  const section = moduleHTML.closest('section[data-js-module-index]')
  return section && section.getAttribute('data-js-module-index') === '0'
}

function shouldElevatePlayer (moduleHTML) {
  return isFirstPageModule(moduleHTML) &&
    typeof window !== 'undefined' &&
    window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH})`).matches
}

function setOverlayPassThrough (doc, enabled) {
  doc.body.classList.toggle('youtube-inline-playing', enabled)
}

function configureYoutubeIframe (player) {
  if (!player || typeof player.getIframe !== 'function') return
  const iframe = player.getIframe()
  if (!iframe) return
  iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin')
  iframe.referrerPolicy = 'strict-origin-when-cross-origin'
}

export function initYoutubeVideo (doc, moduleHTML) {
  if (!moduleHTML || moduleHTML.dataset.jsYoutubeVideoReady === 'true') return
  moduleHTML.dataset.jsYoutubeVideoReady = 'true'

  const videoId = moduleHTML.dataset.jsYoutubeVideoId
  const playButton = moduleHTML.querySelector('[data-js-youtube-play-button]')

  if (!videoId || !playButton) {
    console.error('YouTubeVideo: missing video ID or play button.')
    return
  }

  const playerHost = doc.createElement('div')
  playerHost.className = 'youtube-video-player-host'
  playerHost.setAttribute('data-js-youtube-player-host', 'true')
  moduleHTML.appendChild(playerHost)

  let playing = false
  let touchGestureHandled = false
  let pendingPlay = false
  let gesturePlayRequested = false
  let ytPlayer = null
  let playerReady = false
  let playerReadyPromise = null
  const useIosGesturePlay = isIOSDevice()

  function startPlayerPreload () {
    warmupYoutubeApi(doc)
    return ensurePlayer()
  }

  function ensurePlayer () {
    if (playerReadyPromise) return playerReadyPromise

    playerReadyPromise = loadYoutubeApi(doc).then(() => new Promise((resolve) => {
      if (ytPlayer) {
        resolve(ytPlayer)
        return
      }

      const origin = typeof window !== 'undefined' && window.location && window.location.origin
        ? window.location.origin
        : undefined

      ytPlayer = new window.YT.Player(playerHost, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          playsinline: 1,
          controls: 1,
          rel: 0,
          modestbranding: 1,
          ...(origin ? { origin } : {})
        },
        events: {
          onReady: (event) => {
            playerReady = true
            configureYoutubeIframe(event.target)
            resizePlayer()

            if (pendingPlay && !useIosGesturePlay) {
              pendingPlay = false
              beginPlayback(event.target)
            }

            resolve(event.target)
          },
          onStateChange: (event) => {
            if (event.data === YT_STATE_PLAYING) {
              resizePlayer()
              if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                window.requestAnimationFrame(() => resizePlayer())
              }
            }
          }
        }
      })
    }))

    return playerReadyPromise
  }

  function resizePlayer () {
    if (!ytPlayer || typeof ytPlayer.setSize !== 'function') return
    const { width, height } = moduleHTML.getBoundingClientRect()
    if (width && height) ytPlayer.setSize(Math.floor(width), Math.floor(height))
  }

  function showPlayerSurface () {
    moduleHTML.classList.add('youtube-video--playing')

    if (shouldElevatePlayer(moduleHTML)) {
      moduleHTML.classList.add('youtube-video--elevated')
      setOverlayPassThrough(doc, true)
    }

    if (playButton.parentNode) playButton.remove()
    resizePlayer()
  }

  function beginPlayback (player = ytPlayer) {
    if (!player || typeof player.playVideo !== 'function') return false
    playing = true
    resizePlayer()
    showPlayerSurface()
    player.playVideo()
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => resizePlayer())
    }
    return true
  }

  function playFromGesture () {
    if (playing || !playerReady || !ytPlayer) return false
    return beginPlayback()
  }

  function requestPlayFromGesture () {
    if (playing) return true
    gesturePlayRequested = true
    startPlayerPreload()

    if (playFromGesture()) {
      gesturePlayRequested = false
      return true
    }

    ensurePlayer().then(() => {
      if (gesturePlayRequested && !playing) {
        gesturePlayRequested = false
        playFromGesture()
      }
    })

    return false
  }

  function queuePlayback () {
    if (playing) return
    pendingPlay = true
    startPlayerPreload()
  }

  function handleTouchStart () {
    warmupYoutubeApi(doc)
    startPlayerPreload()
  }

  function handleTouchEnd () {
    if (playing || touchGestureHandled) return
    if (requestPlayFromGesture()) touchGestureHandled = true
  }

  function handleClickPlay (event) {
    if (touchGestureHandled) {
      touchGestureHandled = false
      event.preventDefault()
      event.stopPropagation()
      return
    }
    if (playing) return
    event.preventDefault()
    event.stopPropagation()

    if (requestPlayFromGesture()) return

    if (!useIosGesturePlay) queuePlayback()
  }

  if (useIosGesturePlay) {
    playButton.addEventListener('touchstart', handleTouchStart, { passive: true })
    playButton.addEventListener('touchend', handleTouchEnd, { passive: true })
  }

  playButton.addEventListener('click', handleClickPlay)
  moduleHTML.addEventListener('mouseenter', () => warmupYoutubeApi(doc))
  moduleHTML.addEventListener('focusin', () => warmupYoutubeApi(doc))

  if (useIosGesturePlay && (isFirstPageModule(moduleHTML) || isInOrNearViewport(moduleHTML))) {
    startPlayerPreload()
  }
}
